import { gql } from '@apollo/client';

/**
 * 审核池工作台列表 + 相似诊断（按 hub）。对应 api-server PickupReviewPoolQuery.getReviewPoolListing。
 * 后端为单一源，替换原 mock/reviewPoolMock。
 */
export const GET_REVIEW_POOL_LISTING = gql`
  query GetReviewPoolListing($hubId: Long!, $processStatus: Int, $pageNo: Int!, $pageSize: Int!) {
    getReviewPoolListing(hubId: $hubId, processStatus: $processStatus, pageNo: $pageNo, pageSize: $pageSize) {
      items {
        id
        warehouseId
        addressLine1
        addressLine2
        city
        state
        zipcode
        country
        dock
        contactPersonJson
        channelIdsJson
        channelNames
        sourceTag
        diagnosisType
        standardizeStatus
        similarCount
        similarStations {
          id
          name
          address
          contact
          linkStatus
        }
        addressAnalysisHash
        addressCoreHash
        relatedWarehouseId
        relatedWarehouseName
        hasTodayAppointment
        todayAppointmentId
        todayOrderCount
        processStatus
        contactResult
        proofS3Keys
        secondContactResult
        secondProofS3Keys
        proofDisplayUrls
        secondProofDisplayUrls
        abnormalReportedAt
        abnormalReason
        abnormalRemark
        abnormalProofS3Keys
        abnormalProofDisplayUrls
        abnormalReportedByName
        createdAt
      }
      total
      systemCount
      bdCount
      historyCount
    }
  }
`;

export interface ReviewPoolSimilarStationDto {
  id: number | null;
  name: string | null;
  address: string | null;
  contact: string | null;
  /** 建联状态（cooperation_warehouse.link_status 原值）；非灰度 hub / 历史存量站点为 null */
  linkStatus: number | null;
}

export interface ReviewPoolItemDto {
  id: number | null;
  warehouseId: number | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  country: string | null;
  dock: string | null;
  contactPersonJson: string | null;
  channelIdsJson: string | null;
  channelNames: string[] | null;
  sourceTag: number | null;
  diagnosisType: number | null;
  standardizeStatus: number | null;
  similarCount: number;
  similarStations: ReviewPoolSimilarStationDto[];
  addressAnalysisHash: string | null;
  /** 条目命中键 core_hash：2.1 需求点1 建站时原样回传（skipAddressStandardize），保证与卡单同源 */
  addressCoreHash: string | null;
  relatedWarehouseId: number | null;
  relatedWarehouseName: string | null;
  hasTodayAppointment: boolean;
  todayAppointmentId: number | null;
  /** 当日单量（hub 时区当日要揽收的订单数）；历史存量恒 0 且不展示 */
  todayOrderCount: number;
  processStatus: number | null;
  /**
   * 联系结果草稿（2.1 需求点1）：两步电联的结果与截图，服务端存在 pickup_address_review_pool 上。
   *
   * <p>`*S3Keys` 是原始 objectKey（提交时原样回传），`*DisplayUrls` 是与之等长同序的预签名可读地址
   * （ImageUploader 的 displayUrls 必须与 value 等长才会渲染缩略图，见 ImageUploader.fileListFromProps）。
   */
  contactResult: number | null;
  proofS3Keys: string[];
  secondContactResult: number | null;
  secondProofS3Keys: string[];
  proofDisplayUrls: string[];
  secondProofDisplayUrls: string[];
  /**
   * 地址异常上报（2.2 需求点1）。`abnormalReportedAt` 非空即表示**当前有有效上报** ——
   * 卡片据此展示「已上报地址异常 + 时间 + 原因」与「编辑 / 撤销上报」，并解锁二次建联（免司机上门）。
   * 撤销后这几项一并回到 null / 空数组。
   */
  abnormalReportedAt: string | null;
  abnormalReason: string | null;
  abnormalRemark: string | null;
  abnormalProofS3Keys: string[];
  abnormalProofDisplayUrls: string[];
  abnormalReportedByName: string | null;
  createdAt: string | null;
}

export interface GetReviewPoolListingResponse {
  getReviewPoolListing: {
    items: ReviewPoolItemDto[];
    total: number;
    /**
     * 来源页签角标（2.2 需求点6）。口径与 `total` 一致（同 hub、同 `is_deleted=0 +
     * process_status IN (0,2)`），**不随分页、也不随前端的关键字/日期筛选变化**。
     *
     * ⚠️ 不能在前端按 `items` 统计：列表每页 50 条、首屏只加载一页，数出来的是
     * 「这 50 条里有几条历史存量」。灰度 hub 实测待处理 2966 条。
     *
     * ⚠️ 三项之和**不一定等于 `total`**（`source_tag` 为 NULL 的行只进 total），
     * 所以「全部」页签不显示角标，直接看上方的「待处理 N」。
     */
    systemCount: number;
    bdCount: number;
    historyCount: number;
  };
}

export interface GetReviewPoolListingVariables {
  hubId: number;
  processStatus?: number | null;
  pageNo?: number;
  pageSize?: number;
}
